import { debugLog } from './logger';
import type { GenericAction } from '../../types/actions';

import { API_ENDPOINTS, DEFAULT_CONFIG } from '../constants';
import type { HyperliquidResponse } from '../types';

export interface APIRequestParams {
  action: GenericAction;
  signature: { r: string; s: string; v: number };
  nonce: number;
  vaultAddress?: string;
  expiresAfter?: number;
}

export class HyperliquidAPIClient {
  private baseUrl: string;
  private timeout: number;

  constructor(config: { isTestnet?: boolean; timeout?: number } = {}) {
    this.baseUrl = config.isTestnet ? API_ENDPOINTS.TESTNET : API_ENDPOINTS.MAINNET;
    this.timeout = config.timeout || DEFAULT_CONFIG.TIMEOUT_MS;
  }

  async sendRequest(params: APIRequestParams): Promise<HyperliquidResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const body: Record<string, unknown> = {
        action: params.action,
        signature: params.signature,
        nonce: params.nonce,
      };

      if (params.vaultAddress) {
        body.vaultAddress = params.vaultAddress;
      }

      if (params.expiresAfter) {
        body.expiresAfter = params.expiresAfter;
      }

      // Log the request payload for debugging
      debugLog('[Hyperliquid API] Sending request:', JSON.stringify(body, null, 2));

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({
          error: response.statusText,
        }))) as { error?: string };
        throw new Error(
          `Hyperliquid API error: ${errorData.error || response.statusText}`
        );
      }

      const data = (await response.json()) as HyperliquidResponse;

      if (data.status === 'error') {
        throw new Error(`Hyperliquid error: ${data.response}`);
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${this.timeout}ms`);
        }
        throw error;
      }

      throw new Error('Unknown error occurred');
    }
  }
}
